import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPatch, apiPost } from '../api/http';
import CommentBox from '../components/CommentBox';
import { useAuth } from '../auth/AuthContext';
import { formatIndianNumber, handleMoneyShortcutKeyDown, parseIndianNumber } from '../utils/MoneyShortcuts';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';

function normalizeYes(v) {
  if (v === true) return true;
  if (v === false) return false;
  if (v === 1) return true;
  if (v === 0) return false;
  if (v == null) return false;

  const s = String(v).trim().toLowerCase();
  if (s === 'yes' || s === 'true' || s === '1') return true;
  if (s === 'no' || s === 'false' || s === '0' || s === '') return false;

  return false;
}

function toTriState(valueBool, queriesBool) {
  if (normalizeYes(queriesBool)) return 'Query';
  return normalizeYes(valueBool) ? 'Yes' : 'No';
}

function hasStepQueries(e) {
  return !!(
    e &&
    (e.has_queries || e.hasQueries || e.col_q_idx || e.colQ || e.row_q_idx || e.rowQ)
  );
}

function triStateToFields(next) {
  if (next === 'Query') return { value_bool: false, queries_bool: true };
  return { value_bool: next === 'Yes', queries_bool: false };
}


function normalizeFieldName(name) {
  return String(name || '').trim();
}

const FIELD_RENAMES = new Map([
  ['Data Sheet', 'Sanction Data'],
  ['ESBTR Pending', 'ESBTR'],
  ['Disbursed Date', 'Disbursement Date'],
  ['Insurance Clear', 'Insurance'],
  ['Account Opening Not Required', 'Account Opening'],
  ['Industry', 'Sector'],
]);

function shouldHideFieldByName(name) {
  const s = normalizeFieldName(name).toLowerCase();
  if (!s) return false;
  // Explicit removals (client request)
  if (s === 'clear') return true;
  if (s.includes('credit') && s.includes('quer')) return true;
  if (s.includes('rack rate')) return true;
  if (s.includes('final sanction') && s.includes('expected')) return true;
  if (s === 'credit query' || s === 'credit queries') return true;
  if (s.replace(/\s+/g, '') === 'ltv+foir') return true;
  return false;
}

function renamedLabel(name) {
  const key = normalizeFieldName(name);
  return FIELD_RENAMES.get(key) || key;
}

function isSanctionField(name) {
  return /(^|\s)sanction(\s|$)/i.test(String(name || '').trim());
}

function isNotRequiredField(name) {
  const s = normalizeFieldName(name).toLowerCase();
  return s === 'account opening' || s === 'tsr' || s === 'insurance';
}
function isIssueFlagName(name) {
  const s = String(name || '').trim();
  return /credit\s*queries/i.test(s) || /queries?/i.test(s);
}

function isLoanAmountName(name) {
  return /loan\s*amount/i.test(String(name || '').trim());
}

function isLoginDateName(name) {
  return /login\s*date/i.test(String(name || '').trim());
}

function isCoordinatedByName(name) {
  return /coordinated\s*by/i.test(String(name || '').trim());
}

function isApplicationNoName(name) {
  return /application\s*no/i.test(String(name || '').trim());
}

function isProductName(name) {
  return /product/i.test(String(name || '').trim());
}


function workflowTopRank(name) {
  const s = String(name || '').trim();
  if (isCoordinatedByName(s)) return 0;
  if (isLoanAmountName(s)) return 1;
  if (isProductName(s)) return 2;
  if (isApplicationNoName(s)) return 3;
  if (isLoginDateName(s)) return 4;
  return 999;
}

function isPercentFieldName(name) {
  const s = String(name || '').toLowerCase();
  return s.includes('foir') || s.includes('ltv') || s.includes('double') || s.includes('whammy');
}

function displayPercent(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  // If stored as fraction (0–1), show 0–100.
  if (n >= 0 && n <= 1.2) return String(Math.round(n * 10000) / 100); // 2 decimals max
  return String(n);
}

function parsePercentInput(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  // User enters 80 => store 0.8
  if (n > 1.2) return n / 100.0;
  return n;
}

function sortEntriesForView(entries) {
  return [...(entries || [])].sort((a, b) => {
    const ak = workflowTopRank(a?.name);
    const bk = workflowTopRank(b?.name);
    if (ak !== bk) return ak - bk;

    const ar = a.row_idx ?? a.row ?? 0;
    const br = b.row_idx ?? b.row ?? 0;
    if (ar !== br) return ar - br;
    const ac = a.col_idx ?? a.col ?? 0;
    const bc = b.col_idx ?? b.col ?? 0;
    if (ac !== bc) return ac - bc;
    return (a.id ?? 0) - (b.id ?? 0);
  });
}

/**
 * Packs arbitrary layout into a fixed 3-field-per-row layout:
 *  - HEADER spans full width
 *  - Non-header entries fill Left then Middle then Right
 *  - Comments live below (separate section)
 */
function packIntoThreeColumns(sortedEntries) {
  const rows = [];
  let pendingA = null;
  let pendingB = null;

  for (const e of sortedEntries) {
    const kind = e.kind || e.type;

    if (kind === 'HEADER') {
      if (pendingA || pendingB) {
        rows.push({ type: 'triple', a: pendingA, b: pendingB, c: null });
        pendingA = null;
        pendingB = null;
      }
      rows.push({ type: 'header', header: e });
      continue;
    }

    if (!['INFO', 'STEP', 'DATE', 'FORMULA'].includes(kind)) continue;

    if (!pendingA) pendingA = e;
    else if (!pendingB) pendingB = e;
    else {
      rows.push({ type: 'triple', a: pendingA, b: pendingB, c: e });
      pendingA = null;
      pendingB = null;
    }
  }

  if (pendingA || pendingB) rows.push({ type: 'triple', a: pendingA, b: pendingB, c: null });

  return rows;
}

export default function ProjectBoard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();

  const [entries, setEntries] = useState([]);
  const [project, setProject] = useState(null);

    const [metaDraft, setMetaDraft] = useState({
    property_address: '',
    property_type: '',
    bank_name: '',
    banker_name: '',
    contact_person_1: '',
    contact_person_2: '',
  });
  

  const metaSaveTimerRef = useRef(null);
  const [loading, setLoading] = useState(true);

  // Fast local typing buffer (only used for Loan Amount so it never lags)
  const [draftTextById, setDraftTextById] = useState({});

  const patchTimersRef = useRef(new Map());
  const refreshTimerRef = useRef(null);

  // Prevent refresh from overwriting text while user is actively editing
  const activeEditsRef = useRef(new Set());

  const beginEdit = useCallback((entryId, field) => {
    activeEditsRef.current.add(`${entryId}:${field}`);
  }, []);

  const endEdit = useCallback((entryId, field) => {
    activeEditsRef.current.delete(`${entryId}:${field}`);
  }, []);

  const hasActiveEdits = useCallback(() => activeEditsRef.current.size > 0, []);


    useEffect(() => {
    if (!project) return;
    setMetaDraft({
      property_address: project.property_address || '',
      property_type: project.property_type || '',
      bank_name: project.bank_name || '',
      banker_name: project.banker_name || '',
      contact_person_1: project.contact_person_1 || '',
      contact_person_2: project.contact_person_2 || '',
    });
  }, [project]);
  

  const loadProject = useCallback(async () => {
    try {
      const list = await apiGet('/api/projects');
      const proj = (list || []).find((p) => p.id === parseInt(id, 10));
      setProject(proj || null);
    } catch (err) {
      console.error('Failed to load project', err);
    }
  }, [id]);

  const loadEntries = useCallback(async () => {
    try {
      const data = await apiGet(`/api/projects/${id}/entries`);
      const normalized = (data || []).map((e) => ({
        ...e,
        row_idx: e.row_idx || e.row,
        col_idx: e.col_idx || e.col,
        row_q_idx: e.row_q_idx || e.rowQ,
        col_q_idx: e.col_q_idx || e.colQ,
      }));
      setEntries(normalized);
    } catch (err) {
      console.error('Failed to load entries', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const scheduleMetaSave = useCallback(
    (partial) => {
      if (!project) return;

      const next = { ...metaDraft, ...partial };
      setMetaDraft(next);

      if (metaSaveTimerRef.current) clearTimeout(metaSaveTimerRef.current);
      metaSaveTimerRef.current = setTimeout(async () => {
        try {
          const payload = {};
          for (const k of Object.keys(partial)) payload[k] = next[k] === '' ? null : next[k];

          const updated = await apiPatch(`/api/projects/${id}`, payload);
          setProject(updated);
        } catch (err) {
          console.error('Failed to update project meta', err);
        }
      }, 500);
    },
    [id, project, metaDraft]
  );

  const scheduleRefreshOnce = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    refreshTimerRef.current = setTimeout(() => {
      if (hasActiveEdits()) {
        scheduleRefreshOnce();
        return;
      }
      loadEntries();
    }, 900);
  }, [hasActiveEdits, loadEntries]);

  useEffect(() => {
    loadProject();
    loadEntries();

    return () => {
      for (const t of patchTimersRef.current.values()) clearTimeout(t);
      patchTimersRef.current.clear();
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (metaSaveTimerRef.current) clearTimeout(metaSaveTimerRef.current);
    };
  }, [loadProject, loadEntries]);

  const updateLocalEntryField = useCallback((entryId, field, value) => {
    setEntries((prev) => prev.map((e) => (e.id === entryId ? { ...e, [field]: value } : e)));
  }, []);

  const queuePatch = useCallback(
    (entryId, field, value, debounceMs) => {
      updateLocalEntryField(entryId, field, value);

      const key = `${entryId}:${field}`;
      const existing = patchTimersRef.current.get(key);
      if (existing) clearTimeout(existing);

      const t = setTimeout(async () => {
        try {
          await apiPatch(`/api/projects/${id}/entries/${entryId}`, { [field]: value });
          if (!hasActiveEdits()) scheduleRefreshOnce();
        } catch (err) {
          console.error('Failed to update entry', err);
          alert('Failed to update entry: ' + err.message);
          loadEntries();
        } finally {
          patchTimersRef.current.delete(key);
        }
      }, debounceMs);

      patchTimersRef.current.set(key, t);
    },
    [hasActiveEdits, id, scheduleRefreshOnce, updateLocalEntryField, loadEntries]
  );

  const handleFieldChange = useCallback(
    (entryId, field, value) => {
      // Responsive while still reducing API chatter
      const debounceMs = field === 'value_text' ? 300 : 180;
      queuePatch(entryId, field, value, debounceMs);
    },
    [queuePatch]
  );

  const handleCloseProject = async () => {
    const confirmClose = window.confirm(
      'Are you sure you want to mark this project as completed?\n⚠️ This action cannot be undone.'
    );
    if (!confirmClose) return;

    try {
      await apiPost(`/api/projects/${id}/close`, { comment: 'Closed by admin' });
      alert('✅ Project has been marked as completed.');
      navigate('/projects');
    } catch (err) {
      alert('Failed to close project: ' + err.message);
    }
  };

  const isActive = project?.status === 'active';

  const workflowTopEntries = useMemo(() => {
    const list = (entries || []).filter((e) => {
      const kind = e?.kind || e?.type;
      if (kind !== 'INFO' && kind !== 'DATE') return false;
      // Top workflow row: Coordinated By, Loan Amount, Product, Application No., Login Date
      return workflowTopRank(e?.name) < 5;
    });

    list.sort((a, b) => workflowTopRank(a?.name) - workflowTopRank(b?.name));
    return list.slice(0, 5);
  }, [entries]);

  const workflowTopEntryIdSet = useMemo(() => {
    return new Set(workflowTopEntries.map((e) => e.id));
  }, [workflowTopEntries]);

  const viewRows = useMemo(() => {
    const filtered = (entries || []).filter((e) => !workflowTopEntryIdSet.has(e.id));
    const sorted = sortEntriesForView(filtered);
    return packIntoThreeColumns(sorted);
  }, [entries, workflowTopEntryIdSet]);

  const renderLabel = (text, key) => (
    <Typography
      key={key}
      variant="body2"
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        fontSize: 12.5,
        lineHeight: 1.2,
        pt: 0.2,
      }}
    >
      {text}
    </Typography>
  );

  const renderField = (e, slot) => {
    if (!e) return { label: <Box />, input: <Box /> };

    const kind = e.kind || e.type;
    const entryId = e.id;

    if (shouldHideFieldByName(e.name)) {
      return { label: <Box />, input: <Box /> };
    }

    if (kind === 'INFO') {
      const isLoanAmt = isLoanAmountName(e.name);
      const rawText = (() => {
        if (!isLoanAmt) return (e.value_text || '');
        const draft = draftTextById[entryId];
        if (draft !== undefined) return draft;

        // Source of truth: stored numeric string in value_text (no commas)
        const n = parseIndianNumber(e.value_text);
        return Number.isFinite(n) ? formatIndianNumber(n) : '';
      })();
      const valueText = isPercentFieldName(e.name) && !isLoanAmt ? displayPercent(rawText) : rawText;

      return {
        label: renderLabel(renamedLabel(e.name), `lbl-${slot}-${entryId}`),
        input: (
          <TextField
            key={`inp-${slot}-${entryId}`}
            size="small"
            variant="outlined"
            value={valueText}
            disabled={!isActive}
            inputMode={isLoanAmt ? 'decimal' : undefined}
            onFocus={() => beginEdit(entryId, 'value_text')}
            onChange={(ev) => {
              const next = ev.target.value;

              // Loan Amount: local draft to keep input instant
              if (isLoanAmt) {
                setDraftTextById((prev) => ({ ...prev, [entryId]: next }));
                return;
              }

              handleFieldChange(entryId, 'value_text', next);
            }}
            onKeyDown={(ev) => {
              if (!isActive) return;

              if (isLoanAmt) {
                const current =
                  draftTextById[entryId] ??
                  (Number.isFinite(parseIndianNumber(e.value_text)) ? String(parseIndianNumber(e.value_text)) : '');

                // Only update local draft here. Persist numeric onBlur to avoid ever saving NaN.
                handleMoneyShortcutKeyDown(ev, current, (formatted) => {
                  setDraftTextById((prev) => ({ ...prev, [entryId]: formatted }));
                });
              }
            }}
            onBlur={() => {
              if (isLoanAmt) {
                const raw = draftTextById[entryId];

                // If user didn't actually edit, don't touch persisted value.
                if (raw !== undefined) {
                  const numeric = parseIndianNumber(raw);
                  handleFieldChange(entryId, 'value_text', Number.isFinite(numeric) ? String(numeric) : '');

                  setDraftTextById((prev) => {
                    const copy = { ...prev };
                    delete copy[entryId];
                    return copy;
                  });
                }
              } else if (isPercentFieldName(e.name)) {
                const next = parsePercentInput(valueText);
                if (next != null) handleFieldChange(entryId, 'value_text', String(next));
              }

              endEdit(entryId, 'value_text');
              scheduleRefreshOnce();
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 4, bgcolor: 'background.paper' },
            }}
            fullWidth
          />
        ),
      };
    }

    if (kind === 'STEP') {
      const nameLabel = renamedLabel(e.name);
      const stepYes = normalizeYes(e.value_bool);
      const hasQueries = hasStepQueries(e) || isSanctionField(nameLabel); // Sanction must always support Query
      const isNR = isNotRequiredField(nameLabel);

      // For Not-Required style fields, we store the selection in value_text (Yes/No/Not Required)
      // and keep value_bool unused (no legacy semantics).
      const nrValue = String(e.value_text || '').trim() || 'No';

      const tri = isNR
        ? nrValue
        : (hasQueries ? toTriState(e.value_bool, e.queries_bool) : (stepYes ? 'Yes' : 'No'));

      return {
        label: renderLabel(nameLabel, `lbl-${slot}-${entryId}`),
        input: (
          <TextField
            key={`inp-${slot}-${entryId}`}
            select
            size="small"
            variant="outlined"
            value={tri}
            disabled={!isActive}
            onFocus={() => beginEdit(entryId, isNR ? 'value_text' : 'value_bool')}
            onChange={(ev) => {
              const next = ev.target.value;

              if (isNR) {
                handleFieldChange(entryId, 'value_text', next);
                return;
              }

              if (hasQueries) {
                const patch = triStateToFields(next);
                handleFieldChange(entryId, 'value_bool', patch.value_bool);
                handleFieldChange(entryId, 'queries_bool', patch.queries_bool);
              } else {
                handleFieldChange(entryId, 'value_bool', next === 'Yes');
              }
            }}
            onBlur={() => {
              endEdit(entryId, isNR ? 'value_text' : 'value_bool');
              scheduleRefreshOnce();
            }}
            sx={(t) => {
              const v = tri;
              let bg = 'transparent';
              let brd = t.palette.divider;

              const issueFlag = isIssueFlagName(nameLabel);
              const isQueryProduct = v === 'Query';

              // For NR fields: Not Required should look like Yes (green)
              const isNRGood = isNR && (v === 'Yes' || v === 'Not Required');

              const yesIsGood = isNRGood ? true : (issueFlag ? (v === 'No') : (v === 'Yes'));
              const noIsBad = isNR ? (v === 'No') : (issueFlag ? (v === 'Yes') : (v === 'No'));

              // Client feedback: make status colors darker (less "washed out").
              // We do it via higher alpha + slightly stronger border for both light & dark.
              if (yesIsGood) {
                bg = t.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.26)' : 'rgba(46, 125, 50, 0.16)';
                brd = t.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.62)' : 'rgba(46, 125, 50, 0.38)';
              } else if (noIsBad) {
                bg = t.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.26)' : 'rgba(211, 47, 47, 0.16)';
                brd = t.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.60)' : 'rgba(211, 47, 47, 0.36)';
              } else if (isQueryProduct) {
                bg = t.palette.mode === 'dark' ? 'rgba(245, 124, 0, 0.26)' : 'rgba(245, 124, 0, 0.16)';
                brd = t.palette.mode === 'dark' ? 'rgba(245, 124, 0, 0.62)' : 'rgba(245, 124, 0, 0.38)';
              }

              return {
                '& .MuiOutlinedInput-root': {
                  bgcolor: bg,
                  borderRadius: 3,
                  '& fieldset': { borderColor: brd, borderWidth: 1.5 },
                },
              };
            }}
            fullWidth
          >
            {/* Note: order is No/Yes/Query for sanity */}
            <MenuItem value="No">No</MenuItem>
            <MenuItem value="Yes">Yes</MenuItem>
            {isNR ? <MenuItem value="Not Required">Not Required</MenuItem> : null}
            {!isNR && hasQueries ? <MenuItem value="Query">Query</MenuItem> : null}
          </TextField>
        ),
      };
    }

    if (kind === 'DATE') {
      const value = e.value_date ? String(e.value_date).split('T')[0] : '';
      return {
        label: renderLabel(renamedLabel(e.name), `lbl-${slot}-${entryId}`),
        input: (
          <TextField
            key={`inp-${slot}-${entryId}`}
            type="date"
            size="small"
            variant="outlined"
            value={value}
            disabled={!isActive}
            onFocus={() => beginEdit(entryId, 'value_date')}
            onChange={(ev) => handleFieldChange(entryId, 'value_date', ev.target.value)}
            onBlur={() => {
              endEdit(entryId, 'value_date');
              scheduleRefreshOnce();
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4, bgcolor: 'background.paper' } }}
            fullWidth
          />
        ),
      };
    }

    if (kind === 'FORMULA') {
      return {
        label: renderLabel(renamedLabel(e.name), `lbl-${slot}-${entryId}`),
        input: (
          <TextField
            key={`inp-${slot}-${entryId}`}
            size="small"
            variant="outlined"
            value={e.value_text || ''}
            disabled
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'action.hover' } }}
            fullWidth
          />
        ),
      };
    }

    return { label: <Box />, input: <Box /> };
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 1.25, md: 2 }, py: 2 }}>
      <Stack spacing={2}>
        {/* Header + meta */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 0.9, md: 1.1 },
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Stack spacing={0.75}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  {project?.client_name || 'Project'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.25 }}>
                  {project?.loan_type ? `${project.loan_type} Loan` : ''}
                  {project?.created_at
                    ? ` • Created: ${new Date(project.created_at).toLocaleDateString()}`
                    : ''}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  variant="outlined"
                  label={(project?.status || '—').toString()}
                  color={(project?.status || '').toLowerCase() === 'active' ? 'success' : 'default'}
                />
              </Stack>
            </Stack>

            <Divider />
            <Grid container spacing={0.75} alignItems="center">
              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 95, fontWeight: 700, opacity: 0.9 }}>
                    Bank Name:
                  </Typography>
                  <TextField
                    size="small"
                    value={metaDraft.bank_name}
                    disabled={!isActive}
                    onChange={(e) => scheduleMetaSave({ bank_name: e.target.value })}
                    fullWidth
                    sx={{ maxWidth: 520, '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 130, fontWeight: 700, opacity: 0.9 }}>
                    Banker Name:
                  </Typography>
                  <TextField
                    size="small"
                    value={metaDraft.banker_name}
                    disabled={!isActive}
                    onChange={(e) => scheduleMetaSave({ banker_name: e.target.value })}
                    fullWidth
                    sx={{ maxWidth: 520, '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 130, fontWeight: 700, opacity: 0.9 }}>
                    Contact Person 1:
                  </Typography>
                  <TextField
                    size="small"
                    value={metaDraft.contact_person_1}
                    disabled={!isActive}
                    onChange={(e) => scheduleMetaSave({ contact_person_1: e.target.value })}
                    fullWidth
                    sx={{ maxWidth: 520, '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 130, fontWeight: 700, opacity: 0.9 }}>
                    Contact Person 2:
                  </Typography>
                  <TextField
                    size="small"
                    value={metaDraft.contact_person_2}
                    disabled={!isActive}
                    onChange={(e) => scheduleMetaSave({ contact_person_2: e.target.value })}
                    fullWidth
                    sx={{ maxWidth: 520, '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
                  />
                </Box>
              </Grid>
            </Grid>

            <Box
              sx={{
                mt: 0,
                display: 'grid',
                gap: 0.75,
                alignItems: 'start',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ minWidth: 130, fontWeight: 700, opacity: 0.9, pt: 0.35 }}>
                  Property Address:
                </Typography>

                <TextField
                  size="small"
                  value={metaDraft.property_address}
                  disabled={!isActive}
                  onChange={(e) => scheduleMetaSave({ property_address: e.target.value })}
                  multiline
                  minRows={1}
                  maxRows={2}
                  fullWidth
                  sx={{
                    minWidth: 0,
                    '& .MuiOutlinedInput-root': { borderRadius: 4 },
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ minWidth: 130, fontWeight: 700, opacity: 0.9 }}>
                  Type of Property:
                </Typography>
                <TextField
                  size="small"
                  value={metaDraft.property_type}
                  disabled={!isActive}
                  onChange={(e) => scheduleMetaSave({ property_type: e.target.value })}
                  fullWidth
                  sx={{
                    minWidth: 0,
                    '& .MuiOutlinedInput-root': { borderRadius: 4 },
                  }}
                />
              </Box>
            </Box>
          </Stack>
        </Paper>

        {/* Workflow */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.0, md: 1.35 },
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.75 }}>
            Workflow
          </Typography>


          {!loading && workflowTopEntries.length > 0 ? (
            <Box
              sx={{
                mb: 1.0,
                display: 'grid',
                gap: '14px 18px',
                // Always keep them in the same row on desktop (client asked).
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: '1fr 1fr',
                  md: '1fr 1fr 1fr',
                  lg: 'repeat(5, minmax(210px, 1fr))',
                },
                alignItems: 'start',
              }}
            >
              {workflowTopEntries.map((e, i) => {
                const r = renderField(e, `TOP-${i}`);
                const input = React.cloneElement(r.input, {
                  sx: (t) => ({
                    ...(typeof r.input.props.sx === 'function'
                      ? r.input.props.sx(t)
                      : r.input.props.sx || {}),
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      minHeight: 46,
                      ...(typeof r.input.props.sx === 'function'
                        ? (r.input.props.sx(t)['& .MuiOutlinedInput-root'] || {})
                        : ((r.input.props.sx || {})['& .MuiOutlinedInput-root'] || {})),
                    },
                    '& .MuiOutlinedInput-input': { py: 1.2 },
                    '& .MuiSelect-select': { py: 1.2 },
                  }),
                });

                return (
                  <Box key={`top-${e.id}`}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: 'text.secondary',
                        fontWeight: 900,
                        mb: 0.45,
                        letterSpacing: 0.15,
                      }}
                    >
                      {renamedLabel(e.name)}
                    </Typography>
                    {input}
                  </Box>
                );
              })}
            </Box>
          ) : null}

          {loading ? (
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Loading workflow...
            </Typography>
          ) : viewRows.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              No workflow entries found.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                // Client feedback: add more breathing space between the 3 columns.
                gap: { xs: '14px 16px', md: '18px 26px', lg: '20px 34px' },
                alignItems: 'start',
                // Center the entire 3-column block within the available screen width.
                // We keep the 3-column layout, but use fixed-ish track widths on desktop
                // so the grid doesn't "hug" the left edge on wide screens.
                justifyContent: 'center',
                width: '100%',
                mx: 'auto',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(320px, 1fr))',
                  lg: 'repeat(3, minmax(320px, 420px))',
                },
              }}
            >
              {viewRows.map((row, idx) => {
                if (row.type === 'header') {
                  const h = row.header;
                  return (
                    <Box
                      key={`hdr-${h.id ?? h.name ?? idx}`}
                      sx={{
                        gridColumn: '1 / -1',
                        pt: idx === 0 ? 0 : 1.0,
                        pb: 0.5,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 900,
                          letterSpacing: 0.2,
                          color: 'text.primary',
                          opacity: 0.92,
                        }}
                      >
                        {h.name}
                      </Typography>
                      <Divider sx={{ mt: 0.6 }} />
                    </Box>
                  );
                }

                const a = renderField(row.a, `A-${idx}`);
                const b = renderField(row.b, `B-${idx}`);
                const c = renderField(row.c, `C-${idx}`);

                const keyBase = row.a?.id ?? row.b?.id ?? row.c?.id ?? idx;

                return (
                  <React.Fragment key={`triple-${keyBase}`}>
                    {[row.a, row.b, row.c].filter(Boolean).map((entry, j) => {
                      const r = renderField(entry, `BLK-${idx}-${j}`);

                      return (
                        <Box
                          key={`blk-${entry.id ?? (String(idx) + '-' + String(j))}`}
                          sx={{
                            p: 0.9,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.02)'
                                : 'rgba(0,0,0,0.02)',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: 'text.secondary',
                              fontWeight: 900,
                              mb: 0.5,
                              letterSpacing: 0.15,
                            }}
                          >
                            {renamedLabel(entry.name)}
                          </Typography>

                          {React.cloneElement(r.input, {
                            sx: (t) => ({
                              ...(typeof r.input.props.sx === 'function'
                                ? r.input.props.sx(t)
                                : r.input.props.sx || {}),
                              // Client feedback: controls feel too long. Keep the 3-column layout,
                              // but make each control ~50-60% width on desktop with extra whitespace.
                              width: { xs: '100%', sm: '92%', lg: '60%' },
                              // Center the control inside its tile (so the whitespace is symmetric).
                              mx: { xs: 0, lg: 'auto' },
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                minHeight: 46,
                                ...(typeof r.input.props.sx === 'function'
                                  ? (r.input.props.sx(t)['& .MuiOutlinedInput-root'] || {})
                                  : ((r.input.props.sx || {})['& .MuiOutlinedInput-root'] || {})),
                              },
                              '& .MuiOutlinedInput-input': { py: 1.25 },
                              '& .MuiSelect-select': { py: 1.25 },
                            }),
                          })}
                        </Box>
                      );
                    })}
                  </React.Fragment>
                );

              })}
            </Box>
          )}
        </Paper>

        {/* Comments (full width below) */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.25, md: 1.75 },
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: 'background.paper',
            // CommentBox uses its own heading. Ensure it stays readable in dark theme.
            // Use !important to override Typography variants that set color="primary".
            '& .MuiTypography-root': { color: 'text.primary !important' },
            '& .MuiTypography-colorPrimary': { color: 'text.primary !important' },
            '& .MuiTypography-h6, & .MuiTypography-subtitle1': {
              color: 'text.primary !important',
              fontWeight: 900,
            },
            // In case the heading is rendered as a plain HTML heading tag.
            '& h1, & h2, & h3, & h4, & h5, & h6': { color: 'text.primary !important' },
          }}
        >
          {/* CommentBox already renders its own heading; avoid duplicate "Comments" title here. */}
          <CommentBox projectId={id} canDelete={isActive && user?.role === 'admin'} />
        </Paper>

        {/* Admin actions */}
        {(user?.role === 'admin' || user?.role === 'superadmin') && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, md: 1.75 },
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              {isActive ? (
                <Button variant="contained" color="error" onClick={handleCloseProject}>
                  Mark Project Completed
                </Button>
              ) : (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(46, 125, 50, 0.25)'
                        : 'rgba(46, 125, 50, 0.12)',
                    border: `1px solid ${theme.palette.success.main}`,
                  }}
                >
                  <Typography sx={{ fontWeight: 800, color: 'success.main' }}>
                    ✅ Project Completed
                  </Typography>
                </Box>
              )}

              <Box sx={{ flex: 1 }} />

              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Layout is packed into 3 columns (desktop) to minimize scrolling.
              </Typography>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
